import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Modal } from '@/components/ui/modal';
import { Gamepad2, Search, Loader2, AlertCircle, Copy, MapPin, Globe, Building2, Users } from 'lucide-react';
import { xboxApiService, GameSessionLookupResult } from '@/services/xboxApi';
import { useToast } from '@/components/ui/toast';

const Games: React.FC = () => {
  const [searchInput, setSearchInput] = useState<string>('');
  const [sessionData, setSessionData] = useState<GameSessionLookupResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [friends, setFriends] = useState<Array<{ gamertag: string; xuid: string; isOnline: boolean }>>([]);
  const [friendsModalOpen, setFriendsModalOpen] = useState<boolean>(false);
  const [filterText, setFilterText] = useState<string>('');
  const { toast, Toaster } = useToast();

  // Load friend options from cache
  useEffect(() => {
    const normalizeFromCache = () => {
      const cached = localStorage.getItem('friends_cache');
      if (!cached) return;
      try {
        const parsed = JSON.parse(cached) as Array<any>;
        const list = parsed
          .filter(p => p && (p.xuid || p.gamertag || p.displayName))
          .map(p => ({
            gamertag: p.gamertag || p.displayName,
            xuid: String(p.xuid || ''),
            isOnline: ((p.presenceState || '').toLowerCase() === 'online') || !!p.isOnline,
          }))
          .sort((a: { gamertag: string; isOnline: boolean }, b: { gamertag: string; isOnline: boolean }) =>
            Number(b.isOnline) - Number(a.isOnline) || a.gamertag.localeCompare(b.gamertag)
          );
        setFriends(list);
      } catch {}
    };
    normalizeFromCache();
    const id = window.setInterval(normalizeFromCache, 10000);
    return () => clearInterval(id);
  }, []);

  const handleFetchGameSession = async (identifier?: string) => {
    const lookupId = identifier || searchInput.trim();
    
    if (!lookupId) {
      setError('Please enter a Gamertag or XUID');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSessionData(null);

    try {
      const response = await xboxApiService.lookupGameSession(lookupId);
      
      if (response.error) {
        setError(response.error);
        toast('Failed to fetch game session', 'error');
        return;
      }

      if (response.data) {
        setSessionData(response.data);
        if (!response.data.success && response.data.error) {
          setError(response.data.error);
          toast(response.data.error, 'info');
        } else if (response.data.success && response.data.members && response.data.members.length > 0) {
          toast(`Game session loaded with ${response.data.members.length} member(s)`, 'success');
        } else {
          toast('No active game session found', 'info');
        }
      }
    } catch (err) {
      console.error('Failed to fetch game session:', err);
      setError('Failed to fetch game session. Please try again.');
      toast('Failed to fetch game session', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFetchMySession = async () => {
    // Get current user's XUID from stored user info
    const userInfoRaw = localStorage.getItem('xbox_user_info');
    const xuid = userInfoRaw ? (JSON.parse(userInfoRaw)?.xid || JSON.parse(userInfoRaw)?.xuid) : null;
    
    if (!xuid) {
      setError('Not logged in - XUID not found');
      toast('Not logged in', 'error');
      return;
    }

    await handleFetchGameSession(xuid);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleFetchGameSession();
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast('Copied to clipboard', 'success');
  };

  const members = sessionData?.members || [];

  return (
    <div className="space-y-6">
      <Toaster />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">Game Session</h1>
          <p className="text-muted-foreground mt-2">
            Look up active game sessions and view member IP addresses
          </p>
        </div>
        
        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-center sm:gap-3">
          <Button 
            onClick={handleFetchMySession} 
            disabled={isLoading} 
            className="flex items-center gap-2"
            title="Fetch my game session"
          >
            <Search className="h-4 w-4" />
            {isLoading ? 'Fetching...' : 'My Session'}
          </Button>
          <Button 
            onClick={() => setFriendsModalOpen(true)} 
            variant="secondary" 
            className="flex items-center gap-2"
            title="Look up friend's game session"
          >
            <Users className="h-4 w-4" />
            Friends
          </Button>
        </div>
      </div>

      {/* Friends Modal */}
      <Modal open={friendsModalOpen} onClose={() => setFriendsModalOpen(false)} title="Select a friend">
        <div className="space-y-3">
          <input
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="Filter by gamertag..."
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <div className="max-h-80 overflow-auto divide-y divide-border rounded-md border border-border">
            {friends
              .filter(f => f.gamertag.toLowerCase().includes(filterText.toLowerCase()))
              .slice(0, 200)
              .map((f) => (
                <button
                  key={f.xuid}
                  onClick={() => {
                    setFilterText('');
                    setFriendsModalOpen(false);
                    handleFetchGameSession(f.xuid);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-accent/50 flex items-center justify-between"
                >
                  <span className="truncate flex items-center gap-2">
                    <span className={`inline-block h-2.5 w-2.5 rounded-full ${f.isOnline ? 'bg-green-500' : 'bg-zinc-600'}`} />
                    {f.gamertag}
                  </span>
                  <span className="ml-2 text-xs text-muted-foreground">{f.xuid}</span>
                </button>
              ))}
            {friends.filter(f => f.gamertag.toLowerCase().includes(filterText.toLowerCase())).length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">No matches</div>
            )}
          </div>
        </div>
      </Modal>

      {/* Search Input */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Search by Gamertag or XUID
          </CardTitle>
          <CardDescription>
            Enter any Gamertag or XUID to look up their active game session
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter Gamertag or XUID..."
              className="flex-1 px-4 py-2 rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              disabled={isLoading}
            />
            <Button
              onClick={() => handleFetchGameSession()}
              disabled={isLoading || !searchInput.trim()}
              className="flex w-full items-center justify-center gap-2 sm:w-auto"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Looking up...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4" />
                  Lookup
                </>
              )}
            </Button>
          </div>
          
          {error && (
            <div className="mt-4 p-3 rounded-md bg-destructive/10 text-destructive flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Session Info */}
      {sessionData?.success && sessionData.sessionId && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gamepad2 className="h-5 w-5 text-green-500" />
              Active Game Session
            </CardTitle>
            <CardDescription>
              Session found with {sessionData.memberCount || 0} member(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="p-3 rounded-lg bg-accent/20">
                <div className="text-xs text-muted-foreground mb-1">Session ID</div>
                <div className="font-mono text-sm truncate" title={sessionData.sessionId}>
                  {sessionData.sessionId.substring(0, 8)}...
                  <button
                    onClick={() => copyToClipboard(sessionData.sessionId || '')}
                    className="ml-2 text-muted-foreground hover:text-foreground"
                    title="Copy full session ID"
                  >
                    <Copy className="h-3 w-3 inline" />
                  </button>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-accent/20">
                <div className="text-xs text-muted-foreground mb-1">Template</div>
                <div className="font-medium text-sm">{sessionData.templateName || 'N/A'}</div>
              </div>
              <div className="p-3 rounded-lg bg-accent/20">
                <div className="text-xs text-muted-foreground mb-1">Title ID</div>
                <div className="font-mono text-sm">{sessionData.titleId || 'N/A'}</div>
              </div>
              <div className="p-3 rounded-lg bg-accent/20">
                <div className="text-xs text-muted-foreground mb-1">Visibility</div>
                <div className="font-medium text-sm capitalize">{sessionData.visibility || 'N/A'}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Members Table */}
      <Card>
        <CardHeader>
          <CardTitle>Session Members</CardTitle>
          <CardDescription>
            Players in the game session with their network information
          </CardDescription>
        </CardHeader>
        <CardContent>
          {members.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Gamertag</TableHead>
                    <TableHead>XUID</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Port</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>ISP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member, index) => (
                    <TableRow key={member.xuid || index}>
                      <TableCell className="font-medium">
                        {member.gamertag}
                        {member.isYou && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-green-500/10 px-2 py-1 text-xs font-medium text-green-500">
                            You
                          </span>
                        )}
                        {member.active === false && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-yellow-500/10 px-2 py-1 text-xs font-medium text-yellow-500">
                            Inactive
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {member.xuid}
                        <button
                          onClick={() => copyToClipboard(member.xuid)}
                          className="ml-2 text-muted-foreground hover:text-foreground"
                        >
                          <Copy className="h-3 w-3 inline" />
                        </button>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {member.ipAddress ? (
                          <>
                            {member.ipAddress}
                            <button
                              onClick={() => copyToClipboard(member.ipAddress || '')}
                              className="ml-2 text-muted-foreground hover:text-foreground"
                            >
                              <Copy className="h-3 w-3 inline" />
                            </button>
                          </>
                        ) : (
                          <span className="text-muted-foreground">N/A</span>
                        )}
                      </TableCell>
                      <TableCell>{member.port || 'N/A'}</TableCell>
                      <TableCell>
                        {member.geo?.city || member.geo?.country ? (
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-muted-foreground" />
                            <span>
                              {[member.geo.city, member.geo.country].filter(Boolean).join(', ')}
                            </span>
                            {member.geo.latitude && member.geo.longitude && (
                              <a
                                href={`https://www.google.com/maps?q=${member.geo.latitude},${member.geo.longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-1 text-blue-500 hover:text-blue-400"
                                title="View on Google Maps"
                              >
                                <Globe className="h-3 w-3 inline" />
                              </a>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">N/A</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {member.geo?.isp ? (
                          <div className="flex items-center gap-1">
                            <Building2 className="h-3 w-3 text-muted-foreground" />
                            <span className="truncate max-w-[150px]" title={member.geo.isp}>
                              {member.geo.isp}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">N/A</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              <div className="text-center">
                <Gamepad2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No game session members found</p>
                <p className="text-sm">Use "My Session", "Friends", or search to lookup a game session</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Session Statistics */}
      {members.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Session Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
              <div className="text-center p-4 rounded-lg bg-accent/20">
                <div className="text-2xl font-bold text-foreground">{members.length}</div>
                <div className="text-sm text-muted-foreground">Total Members</div>
              </div>
              <div className="text-center p-4 rounded-lg bg-blue-500/20">
                <div className="text-2xl font-bold text-blue-500">
                  {members.filter(m => m.ipAddress).length}
                </div>
                <div className="text-sm text-muted-foreground">IPs Resolved</div>
              </div>
              <div className="text-center p-4 rounded-lg bg-purple-500/20">
                <div className="text-2xl font-bold text-purple-500">
                  {new Set(members.filter(m => m.geo?.country).map(m => m.geo!.country)).size}
                </div>
                <div className="text-sm text-muted-foreground">Countries</div>
              </div>
              <div className="text-center p-4 rounded-lg bg-orange-500/20">
                <div className="text-2xl font-bold text-orange-500">
                  {new Set(members.filter(m => m.geo?.isp).map(m => m.geo!.isp)).size}
                </div>
                <div className="text-sm text-muted-foreground">ISPs</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="border-yellow-500/30 bg-yellow-500/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-500">Important Note</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Game Session lookup requires the target player to be actively in a game session. 
                The Session Directory API may require console-level authentication for full access.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Games;
